import {
  Dialog as ChakraDialog,
  DialogBackdropProps,
  DialogBodyProps,
  DialogContentProps,
  DialogRootProps,
  PortalProps,
} from '@chakra-ui/react'
import { ReactNode } from 'react'
import { forceLight } from 'utils'
import IconButton from './IconButton'
import { XMarkIcon } from './icons'
import { Portal } from './providers'

export interface Props extends DialogRootProps {
  backdropProps?: DialogBackdropProps
  bodyProps?: Omit<DialogBodyProps, 'children'>
  contentProps?: Omit<DialogContentProps, 'children'>
  footer?: JSX.Element
  hideClose?: boolean
  onClose?: () => void
  portalProps?: PortalProps
  title?: ReactNode
  trigger?: ReactNode
}

export default function Dialog({
  backdropProps,
  bodyProps,
  children,
  contentProps,
  footer,
  hideClose,
  onClose,
  onOpenChange,
  portalProps,
  title,
  trigger,
  ...rest
}: Props) {
  return (
    <ChakraDialog.Root
      onOpenChange={p => {
        onOpenChange?.(p)
        if (!p.open) onClose?.()
      }}
      {...rest}
    >
      <ChakraDialog.Trigger asChild>{trigger}</ChakraDialog.Trigger>
      <Portal {...portalProps}>
        <ChakraDialog.Backdrop {...positionProps} {...backdropProps} />
        <ChakraDialog.Positioner {...positionProps}>
          <ChakraDialog.Content
            onClick={e => e.stopPropagation()}
            {...forceLight}
            maxHeight="80vh"
            {...contentProps}
          >
            {title && (
              <ChakraDialog.Header fontSize={24} pl={6} pr={12} pt={4} pb={2}>
                <ChakraDialog.Title>{title}</ChakraDialog.Title>
                {!hideClose && (
                  <ChakraDialog.CloseTrigger asChild top="8px">
                    <IconButton aria-label="close drawer" icon={<XMarkIcon />} />
                  </ChakraDialog.CloseTrigger>
                )}
              </ChakraDialog.Header>
            )}
            {!hideClose && <ChakraDialog.CloseTrigger right={4} top={4} />}
            <ChakraDialog.Body p={0} overflow="auto" {...bodyProps}>
              {children}
            </ChakraDialog.Body>

            {footer && <ChakraDialog.Footer>{footer}</ChakraDialog.Footer>}
          </ChakraDialog.Content>
        </ChakraDialog.Positioner>
      </Portal>
    </ChakraDialog.Root>
  )
}

/** Override fixed positioning dialogs nested withing content (i.e. template preview and editing) */
const positionProps = { position: 'absolute', inset: 0, height: 'auto', width: 'auto' }
